// Google Analytics 4 API Integration
// This file provides functions to fetch GA4 data for comprehensive analytics

interface GA4Config {
  propertyId: string;
  serviceAccountKey?: string; // For server-side access
  accessToken?: string; // For client-side access
}

interface GA4Response {
  sessions: number;
  users: number;
  newUsers: number;
  pageviews: number;
  bounceRate: number;
  avgSessionDuration: number;
  conversions: number;
  conversionRate: number;
  revenue: number;
  trafficSources: TrafficSourceData[];
  topPages: PageData[];
  userDemographics: UserDemographics;
  deviceBreakdown: DeviceBreakdown[];
  geographicData: GeographicData[];
  conversionPaths: ConversionPath[];
}

interface TrafficSourceData {
  source: string;
  medium: string;
  campaign?: string;
  sessions: number;
  users: number;
  conversions: number;
  revenue: number;
}

interface PageData {
  pagePath: string;
  pageTitle: string;
  pageviews: number;
  uniquePageviews: number;
  avgTimeOnPage: number;
  bounceRate: number;
  exitRate: number;
}

interface UserDemographics {
  ageGroups: { age: string; users: number; sessions: number }[];
  genders: { gender: string; users: number; sessions: number }[];
  interests: { interest: string; users: number; sessions: number }[];
}

interface DeviceBreakdown {
  deviceCategory: string;
  operatingSystem: string;
  browser: string;
  sessions: number;
  users: number;
  conversions: number;
}

interface GeographicData {
  country: string;
  region: string;
  city: string;
  sessions: number;
  users: number;
  conversions: number;
}

interface ConversionPath {
  path: string[];
  conversions: number;
  revenue: number;
  avgDaysToConversion: number;
}

// Configuration using your existing GA4 property
const GA4_CONFIG: GA4Config = {
  propertyId: process.env.REACT_APP_GA_PROPERTY_ID || '500029829',
  serviceAccountKey: process.env.REACT_APP_GA_SERVICE_ACCOUNT_KEY,
  accessToken: process.env.REACT_APP_GA_ACCESS_TOKEN,
};

/**
 * Fetch comprehensive GA4 analytics data
 */
export async function fetchGA4Data(dateRange: string = '7'): Promise<GA4Response> {
  try {
    // For now, return enhanced mock data
    // TODO: Replace with actual GA4 API calls when credentials are available
    
    if (!GA4_CONFIG.propertyId) {
      console.warn('GA4 not configured, using mock data');
      return getMockGA4Data();
    }

    // Calculate date range for future API implementation
    // const endDate = new Date();
    // const startDate = new Date();
    // startDate.setDate(endDate.getDate() - parseInt(dateRange));
    // const startDateStr = startDate.toISOString().split('T')[0];
    // const endDateStr = endDate.toISOString().split('T')[0];

    // TODO: Implement actual GA4 API calls using date range
    // This would require setting up GA4 Reporting API
    // For now, returning realistic mock data based on your property

    return getMockGA4Data();

  } catch (error) {
    console.error('Error fetching GA4 data:', error);
    return getMockGA4Data();
  }
}

/**
 * Enhanced mock data for development/testing
 */
function getMockGA4Data(): GA4Response {
  return {
    sessions: 2847,
    users: 2156,
    newUsers: 1823,
    pageviews: 8934,
    bounceRate: 0.42,
    avgSessionDuration: 185, // seconds
    conversions: 89,
    conversionRate: 0.031,
    revenue: 267500, // $267,500 in tracked revenue
    trafficSources: [
      {
        source: 'google',
        medium: 'cpc',
        campaign: 'Pergolas Toronto',
        sessions: 1245,
        users: 987,
        conversions: 34,
        revenue: 136000
      },
      {
        source: 'google',
        medium: 'organic',
        sessions: 892,
        users: 743,
        conversions: 28,
        revenue: 84000
      },
      {
        source: 'facebook',
        medium: 'cpc',
        campaign: 'Outdoor Living',
        sessions: 456,
        users: 398,
        conversions: 15,
        revenue: 30000
      },
      {
        source: 'direct',
        medium: '(none)',
        sessions: 254,
        users: 198,
        conversions: 12,
        revenue: 17500
      }
    ],
    topPages: [
      {
        pagePath: '/pergolas',
        pageTitle: 'Custom Pergolas Toronto | Evie Outdoors',
        pageviews: 3245,
        uniquePageviews: 2876,
        avgTimeOnPage: 245,
        bounceRate: 0.35,
        exitRate: 0.28
      },
      {
        pagePath: '/awnings',
        pageTitle: 'Retractable Awnings | Evie Outdoors',
        pageviews: 2134,
        uniquePageviews: 1987,
        avgTimeOnPage: 198,
        bounceRate: 0.41,
        exitRate: 0.33
      },
      {
        pagePath: '/pergola-quote',
        pageTitle: 'Get Pergola Quote | Evie Outdoors',
        pageviews: 1876,
        uniquePageviews: 1654,
        avgTimeOnPage: 312,
        bounceRate: 0.22,
        exitRate: 0.15
      },
      {
        pagePath: '/',
        pageTitle: 'Evie Outdoors | Custom Pergolas & Awnings Toronto',
        pageviews: 1543,
        uniquePageviews: 1398,
        avgTimeOnPage: 156,
        bounceRate: 0.48,
        exitRate: 0.31
      }
    ],
    userDemographics: {
      ageGroups: [
        { age: '25-34', users: 645, sessions: 892 },
        { age: '35-44', users: 734, sessions: 1023 },
        { age: '45-54', users: 523, sessions: 687 },
        { age: '55-64', users: 254, sessions: 245 }
      ],
      genders: [
        { gender: 'Male', users: 1234, sessions: 1687 },
        { gender: 'Female', users: 922, sessions: 1160 }
      ],
      interests: [
        { interest: 'Home & Garden', users: 1876, sessions: 2456 },
        { interest: 'Home Improvement', users: 1234, sessions: 1687 },
        { interest: 'Outdoor Recreation', users: 987, sessions: 1245 }
      ]
    },
    deviceBreakdown: [
      {
        deviceCategory: 'Desktop',
        operatingSystem: 'Windows',
        browser: 'Chrome',
        sessions: 1456,
        users: 1123,
        conversions: 52
      },
      {
        deviceCategory: 'Mobile',
        operatingSystem: 'iOS',
        browser: 'Safari',
        sessions: 892,
        users: 734,
        conversions: 23
      },
      {
        deviceCategory: 'Mobile',
        operatingSystem: 'Android',
        browser: 'Chrome',
        sessions: 499,
        users: 299,
        conversions: 14
      }
    ],
    geographicData: [
      { country: 'Canada', region: 'Ontario', city: 'Toronto', sessions: 1234, users: 987, conversions: 45 },
      { country: 'Canada', region: 'Ontario', city: 'Mississauga', sessions: 567, users: 456, conversions: 18 },
      { country: 'Canada', region: 'Ontario', city: 'Brampton', sessions: 345, users: 289, conversions: 12 },
      { country: 'Canada', region: 'Ontario', city: 'Markham', sessions: 234, users: 198, conversions: 8 },
      { country: 'Canada', region: 'Ontario', city: 'Vaughan', sessions: 189, users: 156, conversions: 6 }
    ],
    conversionPaths: [
      {
        path: ['Google Ads', 'Pergolas Page', 'Quote Form'],
        conversions: 34,
        revenue: 136000,
        avgDaysToConversion: 2.3
      },
      {
        path: ['Organic Search', 'Homepage', 'Pergolas Page', 'Quote Form'],
        conversions: 28,
        revenue: 84000,
        avgDaysToConversion: 4.1
      },
      {
        path: ['Facebook Ads', 'Awnings Page', 'Quote Form'],
        conversions: 15,
        revenue: 30000,
        avgDaysToConversion: 1.8
      }
    ]
  };
}

/**
 * Setup instructions for GA4 API
 */
export const GA4_SETUP_INSTRUCTIONS = `
To connect real Google Analytics 4 data:

1. Go to Google Cloud Console: https://console.cloud.google.com/
2. Enable Google Analytics Reporting API
3. Create a Service Account for server-side access OR
4. Set up OAuth for client-side access
5. Add these environment variables:

For Service Account (Recommended):
REACT_APP_GA_PROPERTY_ID=500029829
REACT_APP_GA_SERVICE_ACCOUNT_KEY=your_service_account_json

For OAuth (Alternative):
REACT_APP_GA_PROPERTY_ID=500029829
REACT_APP_GA_ACCESS_TOKEN=your_access_token

6. Replace mock data with actual GA4 API calls
7. Test with small date ranges first
`;
